import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Upload, message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import { cy } from './DiagramCanvas';
import { EdgeType, MMEdge } from '../model/edge-model';
import { getEdgeColor } from './EdgeEditDialog';
import {
  buildOplContext,
  generateOplSentence,
  OplEdgeRecord,
  OplNodeRecord,
} from '../utils/opl-rules';

const MAX_LABEL_LENGTH = 20;
const MAX_DESCRIPTION_LENGTH = 100;
// Label 合法字符规则：中文、大小写字母、数字、英文下划线，且不能以下划线开头
const LABEL_VALID_PATTERN =
  /^[a-zA-Z\u4e00-\u9fa50-9][a-zA-Z\u4e00-\u9fa50-9_]*$/;

interface EdgeEditSidebarProps {
  visible: boolean;
  selectedEdgeId: string | null;
  onClose: () => void;
  offsetRight?: number;
}

const EDGE_TYPE_LABELS: Record<string, string> = {
  [EdgeType.Aggregation]: '聚合关系',
  [EdgeType.Exhibition]: '展示关系',
  [EdgeType.Generalization]: '泛化关系',
  [EdgeType.Classification]: '实例化关系',
  [EdgeType.Tagged]: '单向关联关系',
  [EdgeType.Bidirectional]: '对等关联关系',
  [EdgeType.Agent]: 'Agent使能',
  [EdgeType.Instrument]: '工具',
  [EdgeType.Consumption]: '消耗',
  [EdgeType.Generation]: '生成',
  [EdgeType.Effect]: '影响',
  [EdgeType.Invocation]: '调用',
};

const EdgeEditSidebar: React.FC<EdgeEditSidebarProps> = ({
  visible,
  selectedEdgeId,
  onClose,
  offsetRight = 0,
}) => {
  const width = 260;
  const [label, setLabel] = useState('');
  const [labelError, setLabelError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const editingEdgeIdRef = useRef<string | null>(null);

  const currentEdge = useMemo(() => {
    if (!cy || !selectedEdgeId) return null;
    const edge = cy.getElementById(selectedEdgeId);
    return edge && edge.length > 0 ? edge : null;
  }, [selectedEdgeId]);

  const edgeRef = useMemo(() => {
    const mmRef = currentEdge?.data('MMRef') as MMEdge | null;
    if (mmRef?.originalEdges?.length) {
      return mmRef.originalEdges[0] || mmRef;
    }
    return mmRef;
  }, [currentEdge]);

  useEffect(() => {
    if (edgeRef) {
      setLabel(edgeRef.label || '');
      setLabelError(null);
      setDescription(edgeRef.description || '');
      if (edgeRef.instanceFileName) {
        setFileList([
          {
            uid: `${edgeRef.id}-instance`,
            name: edgeRef.instanceFileName,
            url: edgeRef.instanceFileUrl,
            status: 'done',
          },
        ]);
      } else {
        setFileList([]);
      }
    } else {
      setLabel('');
      setLabelError(null);
      setDescription('');
      setFileList([]);
    }
  }, [edgeRef]);

  // 记录首次编辑前的快照（仅用于保存时判断是否有真实变更）
  const buildEdgeSnapshot = (ref: MMEdge) => ({
    label: ref?.label || '',
    tags: Array.isArray(ref?.tags) ? [...ref.tags] : [],
    description: ref?.description || '',
    instanceFileName: ref?.instanceFileName || '',
    instanceFileUrl: ref?.instanceFileUrl || '',
  });

  const ensureEdgeSnapshot = (ref: MMEdge) => {
    if (!ref) return;
    if (!ref.__opmcvEditSnapshot) {
      ref.__opmcvEditSnapshot = buildEdgeSnapshot(ref);
    }
  };

  const markEdgeDirty = (ref: MMEdge) => {
    if (!ref) return;
    ref.__opmcvDirty = true;
  };

  const getOwnerEdges = (ref: MMEdge | null) => {
    if (!ref) return [];
    return ref.originalEdges?.length ? ref.originalEdges : [ref];
  };

  const getActiveEdge = () => {
    const edgeId = editingEdgeIdRef.current || currentEdge?.id();
    if (!cy || !edgeId) return null;
    const edge = cy.getElementById(edgeId);
    if (!edge || edge.length === 0) return null;
    return edge;
  };

  const applyToOwnerEdges = (
    ref: MMEdge | null,
    updater: (edge: MMEdge) => void
  ) => {
    if (!ref) return;
    const ownerEdges = getOwnerEdges(ref);
    ownerEdges.forEach(updater);
    if (ref.originalEdges?.length) {
      updater(ref);
    }
  };

  const syncEdgeData = (affected: Set<MMEdge>) => {
    if (!cy || !affected.size) return;
    cy.edges().forEach((edge: any) => {
      const ref = edge.data('MMRef') as MMEdge | null;
      if (!ref) return;
      const shouldUpdate =
        affected.has(ref) ||
        ref.originalEdges?.some(orig => affected.has(orig));
      if (!shouldUpdate) return;
      const displayLabel = ref.originalEdges?.length
        ? ref.originalEdges[0]?.label || ''
        : ref.label || '';
      edge.data({ label: displayLabel });
    });
  };

  const handleSaveLabel = (nextLabel = label) => {
    const edge = getActiveEdge();
    if (!edge) return false;
    const mmRef = edge.data('MMRef') as MMEdge | null;
    if (!mmRef) return false;
    const trimmed = nextLabel.trim();
    // 允许 Label 为空（清空场景）
    if (trimmed !== '' && !LABEL_VALID_PATTERN.test(trimmed)) {
      const errMsg =
        'Label 只能包含中文、字母、数字、下划线，且不能以下划线开头';
      setLabelError(errMsg);
      return false;
    }
    setLabelError(null);
    setLabel(trimmed);
    const affected = new Set<MMEdge>();
    applyToOwnerEdges(mmRef, edgeRefItem => {
      if (edgeRefItem.label !== trimmed) {
        ensureEdgeSnapshot(edgeRefItem);
        markEdgeDirty(edgeRefItem);
      }
      edgeRefItem.label = trimmed;
      affected.add(edgeRefItem);
    });
    syncEdgeData(affected);
    return true;
  };

  const handleSaveDescription = (nextDescription = description) => {
    const edge = getActiveEdge();
    if (!edge) return;
    const mmRef = edge.data('MMRef') as MMEdge | null;
    if (!mmRef) return;
    const normalized = nextDescription || '';
    const affected = new Set<MMEdge>();
    applyToOwnerEdges(mmRef, edgeRefItem => {
      if (edgeRefItem.description !== normalized) {
        ensureEdgeSnapshot(edgeRefItem);
        markEdgeDirty(edgeRefItem);
      }
      edgeRefItem.description = normalized;
      affected.add(edgeRefItem);
    });
    setDescription(normalized);
    syncEdgeData(affected);
  };

  const applyInstanceFile = (file?: UploadFile | null) => {
    const edge = getActiveEdge();
    if (!edge) return;
    const mmRef = edge.data('MMRef') as MMEdge | null;
    if (!mmRef) return;
    const fileName = file?.name || '';
    const fileUrl =
      (file as any)?.response?.fileUrl ||
      (file as any)?.response?.url ||
      file?.url ||
      '';
    applyToOwnerEdges(mmRef, edgeRefItem => {
      if (
        edgeRefItem.instanceFileName !== fileName ||
        edgeRefItem.instanceFileUrl !== fileUrl
      ) {
        ensureEdgeSnapshot(edgeRefItem);
        markEdgeDirty(edgeRefItem);
      }
      edgeRefItem.instanceFileName = fileName;
      edgeRefItem.instanceFileUrl = fileUrl;
    });
    edge.data({ instanceFileName: fileName, instanceFileUrl: fileUrl });
  };

  const handleUploadRequest = (options: any) => {
    const { file, onSuccess, onError } = options;
    try {
      setTimeout(() => {
        onSuccess?.({}, file as any);
      }, 800);
    } catch (error) {
      onError?.(error as any);
    }
  };

  const handleUploadChange = (info: {
    file: UploadFile;
    fileList: UploadFile[];
  }) => {
    const nextList = info.fileList.slice(-1);
    setFileList(nextList);
    if (info.file.status === 'done') {
      applyInstanceFile(info.file);
      message.success('上传成功（已模拟）');
    }
  };

  const handleUploadRemove = () => {
    applyInstanceFile(null);
    setFileList([]);
  };

  // 仅单向关联关系和对等关联关系显示 Label 字段
  const isLabelEdge = useMemo(() => {
    const type = currentEdge?.data('type');
    return type === EdgeType.Tagged || type === EdgeType.Bidirectional;
  }, [currentEdge]);

  const relationTypeLabel = useMemo(() => {
    const type = currentEdge?.data('type');
    return EDGE_TYPE_LABELS[type] || String(type || '-');
  }, [currentEdge]);

  const directionInfo = useMemo(() => {
    if (!currentEdge) return null;
    const source = currentEdge.source();
    const target = currentEdge.target();
    const sourceType = source?.data?.('type');
    const isProcessSource = sourceType === 'process';
    const sourceRole = isProcessSource ? 'Source' : 'source';
    const targetRole = isProcessSource ? 'sink' : 'SInk';
    const sourceLabelRaw =
      source?.data?.('label') || source?.id?.() || '（未命名）';
    const targetLabelRaw =
      target?.data?.('label') || target?.id?.() || '（未命名）';
    const sourceLabel =
      sourceType === 'state'
        ? `在${sourceLabelRaw}的情况下 ${
            source.parent?.()?.data?.('label') ||
            (source.parent?.() as any)?.id?.() ||
            '（未命名）'
          }`
        : sourceLabelRaw;
    return {
      sourceLabel,
      targetLabel: targetLabelRaw,
      sourceColor: getEdgeColor(source?.data?.('type')),
      targetColor: getEdgeColor(target?.data?.('type')),
      sourceRole,
      targetRole,
    };
  }, [currentEdge]);

  const endpointRecords = useMemo(() => {
    const recordMap = new Map<string, OplNodeRecord>();
    const pushMM = (node: any) => {
      if (!node || !node.id || recordMap.has(node.id)) return;
      recordMap.set(node.id, {
        id: node.id,
        label: node.label || node.id,
        type: node.type,
        parentId: (node.parent as any)?.id || '',
        childGroupType: node.childGroupType,
        essence: node.essence,
        affiliation: node.affiliation,
      });
      if (node.type === 'object') {
        (node.children || []).forEach((child: any) => {
          if (child.type === 'state' && !child.deleted) {
            pushMM(child);
          }
        });
      }
      if (node.type === 'state') {
        const parentMM = node.parent as any;
        if (parentMM?.id) {
          pushMM(parentMM);
        }
      }
    };

    const pushCy = (node: any) => {
      if (!node || !node.id) return;
      const id = typeof node.id === 'function' ? node.id() : node.id;
      if (!id || recordMap.has(id)) return;
      const data = node.data ? node.data() : {};
      const parentId =
        data.parent || (node.isChild?.() ? node.parent()?.id?.() : '');
      recordMap.set(id, {
        id,
        label: data.label || data.displayLabel || id,
        type: data.type,
        parentId,
        childGroupType: data.childGroupType,
        essence: data.essence,
        affiliation: data.affiliation,
      });
      if (data.type === 'object' && node.children) {
        const stateChildren = node.children('[type = "state"]');
        stateChildren?.forEach((child: any) => pushCy(child));
      }
      if (data.type === 'state' && parentId) {
        const parentNode = node.parent?.();
        if (parentNode && parentNode.length) {
          pushCy(parentNode);
        }
      }
    };

    if (!currentEdge) {
      return { nodes: [] as OplNodeRecord[], sourceId: '', targetId: '' };
    }

    const sourceNode = currentEdge.source();
    const targetNode = currentEdge.target();
    const sourceMM = sourceNode?.data?.('MMRef');
    const targetMM = targetNode?.data?.('MMRef');

    if (sourceMM) pushMM(sourceMM);
    if (targetMM) pushMM(targetMM);
    if (!sourceMM && sourceNode) pushCy(sourceNode);
    if (!targetMM && targetNode) pushCy(targetNode);

    return {
      nodes: Array.from(recordMap.values()),
      sourceId: sourceMM?.id || sourceNode?.id?.() || '',
      targetId: targetMM?.id || targetNode?.id?.() || '',
    };
  }, [currentEdge]);

  const oplContext = useMemo(() => {
    if (!endpointRecords.nodes.length) return null;
    return buildOplContext(endpointRecords.nodes);
  }, [endpointRecords]);

  const oplPreviewText = useMemo(() => {
    if (!oplContext || !endpointRecords.sourceId || !endpointRecords.targetId) {
      return '';
    }
    const edgeType =
      edgeRef?.type || (currentEdge?.data('type') as EdgeType | undefined);
    if (!edgeType) return '';
    const edgeRecord: OplEdgeRecord = {
      id: edgeRef?.id || currentEdge?.id?.() || 'edge-preview',
      sourceId: endpointRecords.sourceId,
      targetId: endpointRecords.targetId,
      type: edgeType,
      label: edgeRef?.label || currentEdge?.data('label') || '',
      linkQualifier:
        edgeRef?.linkQualifier || currentEdge?.data('linkQualifier'),
      linkNegation: edgeRef?.linkNegation || currentEdge?.data('linkNegation'),
    };
    return generateOplSentence(edgeRecord, oplContext) || '';
  }, [edgeRef, currentEdge, oplContext, endpointRecords]);

  const oplPreviewContent = useMemo(() => {
    if (!oplPreviewText || !oplContext) return oplPreviewText;
    const nodeInfos = endpointRecords.nodes.map(node => ({
      id: node.id,
      label: node.label,
      color: getEdgeColor(node.type as string),
    }));
    if (!nodeInfos.length) return oplPreviewText;

    const escapeReg = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const text = oplPreviewText;
    const hits: { start: number; end: number; color: string }[] = [];
    nodeInfos.forEach(info => {
      const pattern = escapeReg(info.label).replace(/\\\s+/g, '\\s+');
      const regex = new RegExp(pattern, 'gi');
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        hits.push({
          start: match.index,
          end: match.index + match[0].length,
          color: info.color,
        });
        if (match.index === regex.lastIndex) regex.lastIndex++;
      }
    });
    if (!hits.length) return text;

    hits.sort((a, b) =>
      a.start === b.start
        ? b.end - b.start - (a.end - a.start)
        : a.start - b.start
    );
    const merged: typeof hits = [];
    hits.forEach(hit => {
      const last = merged[merged.length - 1];
      if (!last || hit.start >= last.end) merged.push(hit);
    });

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    merged.forEach(hit => {
      if (cursor < hit.start) parts.push(text.slice(cursor, hit.start));
      parts.push(
        <span
          key={`${hit.start}-${hit.end}`}
          style={{ color: hit.color, fontWeight: 600 }}
        >
          {text.slice(hit.start, hit.end)}
        </span>
      );
      cursor = hit.end;
    });
    if (cursor < text.length) parts.push(text.slice(cursor));
    return parts;
  }, [oplPreviewText, oplContext, endpointRecords.nodes]);

  const displayVersion = edgeRef?.version || '1.0';
  const displayUpdatedAt = edgeRef?.updatedAt || '';
  const displayUpdatedBy = Array.isArray(edgeRef?.updatedBy)
    ? edgeRef?.updatedBy
    : [];

  return (
    <div
      className={`node-editor-sidebar ${visible ? 'visible' : ''} text-left`}
      style={{ right: offsetRight, width }}
    >
      <div className="node-editor-sidebar-header">
        <h3>关系信息</h3>
        <button
          className="node-editor-sidebar-close"
          onClick={onClose}
          title="关闭"
        >
          ×
        </button>
      </div>

      <div className="node-editor-sidebar-content text-left">
        {currentEdge ? (
          <>
            <div className="node-editor-form">
              <div className="node-editor-form-row !items-center">
                <div className="node-editor-form-label">关系类型</div>
                <div className="node-editor-form-field !items-center min-h-[32px]">
                  <span className="text-xs text-gray-600">
                    {relationTypeLabel}
                  </span>
                </div>
              </div>

              {isLabelEdge && (
                <div className="node-editor-form-row">
                  <div className="node-editor-form-label">Tag</div>
                  <div className="node-editor-form-field">
                    <div className="flex flex-col w-full">
                      <Input
                        value={label}
                        placeholder="请输入 Tag"
                        maxLength={MAX_LABEL_LENGTH}
                        showCount
                        status={labelError ? 'error' : undefined}
                        onChange={e => {
                          setLabel(e.target.value);
                          if (labelError) setLabelError(null);
                        }}
                        onFocus={() => {
                          editingEdgeIdRef.current = currentEdge?.id() || null;
                        }}
                        onBlur={e => {
                          const ok = handleSaveLabel(e.target.value);
                          if (ok) editingEdgeIdRef.current = null;
                        }}
                        onPressEnter={e =>
                          (e.target as HTMLInputElement).blur()
                        }
                      />
                      {labelError && (
                        <div className="text-xs text-red-500 mt-1">
                          {labelError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="node-editor-form-row">
                <div className="node-editor-form-label">描述</div>
                <div className="node-editor-form-field">
                  <Input.TextArea
                    value={description}
                    placeholder="请输入描述"
                    rows={3}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    showCount
                    onChange={e => setDescription(e.target.value)}
                    onFocus={() => {
                      editingEdgeIdRef.current = currentEdge?.id() || null;
                    }}
                    onBlur={e => {
                      handleSaveDescription(e.target.value);
                      editingEdgeIdRef.current = null;
                    }}
                  />
                </div>
              </div>

              <div className="node-editor-form-row !items-center">
                <div className="node-editor-form-label">关系对象及角色</div>
                <div className="node-editor-form-field !items-center min-h-[32px]">
                  <div className="flex flex-col gap-2 text-xs text-gray-600">
                    {directionInfo ? (
                      <>
                        <div>
                          <span style={{ color: directionInfo.sourceColor }}>
                            {directionInfo.sourceLabel}
                          </span>
                          ，{directionInfo.sourceRole}
                        </div>
                        <div>
                          <span style={{ color: directionInfo.targetColor }}>
                            {directionInfo.targetLabel}
                          </span>
                          ，{directionInfo.targetRole}
                        </div>
                      </>
                    ) : (
                      <div>-</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="node-editor-form-row !items-center">
                <div className="node-editor-form-label">OPL</div>
                <div className="node-editor-form-field !items-center min-h-[32px]">
                  <div className="text-xs text-gray-600">
                    {oplPreviewContent || '-'}
                  </div>
                </div>
              </div>

              <div className="node-editor-form-row !items-center">
                <div className="node-editor-form-label">关系实例化</div>
                <div className="node-editor-form-field !items-center min-h-[32px]">
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={() => message.info('该功能正在开发中...')}
                  >
                    上传jar
                  </Button>
                </div>
              </div>

              {/* 版本、更新时间、更新人字段已隐藏，数据仍在后台维护 */}
            </div>
          </>
        ) : (
          <div className="empty-state">请选择一条关系进行编辑</div>
        )}
      </div>
    </div>
  );
};

export default EdgeEditSidebar;
